import axios from "axios";
import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Card,
    Input,
    Row,
    Col,
    Select,
    Modal,
    Form,
    Button,
    DatePicker,
    Typography,
} from "antd";
import { PlusOutlined, DownloadOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Title } = Typography;

export default function FinalSalaryStatement() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { backUrl, authData } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        month: "",
        year: "",
        organization: "",
        department: "",
        designation: "",
    });

    const [organizationNames, setOrganizationNames] = useState([]);
    const [departmentNames, setDepartmentNames] = useState([]);
    const [designationNames, setDesignationNames] = useState([]);

    // Publish Payslip modal
    const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
    const [generateForm] = Form.useForm();
    const [generating, setGenerating] = useState(false);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    /* ─────────────────────── API ─────────────────────── */
    const fetchData = async () => {
        try {
            setLoading(true);
            // FinalSalaryStatement uses "status" field (not salary_status / approve_status)
            // Server-side filter by month + year when set
            const params = new URLSearchParams();
            params.append("status", "Approved");
            if (filters.month) params.append("month", filters.month);
            if (filters.year)  params.append("year",  filters.year);

            const res = await axios.get(
                `${backUrl}/api/payroll/final-statements/?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const results = res.data.results || [];
            setData(results);
            setFilteredData(results);
            setSelectedRowKeys([]); // clear stale selections
        } catch (error) {
            console.error("Error fetching final salary statement:", error);
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizationNames(res.data.results || []);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartmentNames(res.data.results || []);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignationNames(res.data.results || []);
        } catch (err) {
            console.error("Error fetching designations", err);
        }
    };

    /* ─────────────────────── Effects ─────────────────────── */
    // Re-fetch from server when month or year changes
    useEffect(() => {
        fetchData();
    }, [filters.month, filters.year]);

    useEffect(() => {
        fetchOrganizations();
    }, []);

    // Client-side filtering for org / dept / designation / name / emp_code
    useEffect(() => {
        let filtered = [...data];
        if (filters.organization)
            filtered = filtered.filter(i => i.employee?.organisation_name === filters.organization);
        if (filters.department)
            filtered = filtered.filter(i => i.employee?.department_name === filters.department);
        if (filters.designation)
            filtered = filtered.filter(i => i.employee?.designation_name === filters.designation);
        if (filters.emp_code)
            filtered = filtered.filter(i =>
                i.employee?.emp_code?.toLowerCase().includes(filters.emp_code.toLowerCase())
            );
        if (filters.name)
            filtered = filtered.filter(i => {
                const full = `${i.employee?.first_name || ""} ${i.employee?.last_name || ""}`.toLowerCase();
                return full.includes(filters.name.toLowerCase());
            });
        setFilteredData(filtered);
    }, [filters.organization, filters.department, filters.designation, filters.emp_code, filters.name, data]);

    /* ─────────────────────── Filter Handlers ─────────────────────── */
    const handleOrgChange = (orgId) => {
        const org = organizationNames.find(o => o.id === orgId);
        setFilters({ ...filters, organization: org?.name || "", department: "", designation: "" });
        setDepartmentNames([]);
        setDesignationNames([]);
        if (orgId) fetchDepartments(orgId);
    };

    const handleDeptChange = (deptId) => {
        const dept = departmentNames.find(d => d.id === deptId);
        setFilters({ ...filters, department: dept?.name || "", designation: "" });
        setDesignationNames([]);
        if (deptId) fetchDesignations(deptId);
    };

    const handleDesignationChange = (desId) => {
        const des = designationNames.find(d => d.id === desId);
        setFilters({ ...filters, designation: des?.name || "" });
    };

    const handleResetFilters = () => {
        setFilters({ name: "", emp_code: "", month: "", year: "", organization: "", department: "", designation: "" });
        setDepartmentNames([]);
        setDesignationNames([]);
    };

    /* ─────────────────────── Publish Payslip ─────────────────────── */
    const handleOpenGenerateModal = () => {
        if (selectedRowKeys.length === 0) {
            toast.warning("Please select at least one record!");
            return;
        }
        generateForm.resetFields();
        setIsGenerateModalVisible(true);
    };

    const handleCloseGenerateModal = () => {
        setIsGenerateModalVisible(false);
        generateForm.resetFields();
    };

    const handleGeneratePayslip = async () => {
        try {
            const values = await generateForm.validateFields();
            const { month_year } = values;
            const month = dayjs(month_year).month() + 1;
            const year  = dayjs(month_year).year();

            setGenerating(true);

            const res = await axios.post(
                `${backUrl}/api/payroll/publish-payslips/`,
                { ids: selectedRowKeys, month, year },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success(res.data.message || "Payslips published successfully!");
            setSelectedRowKeys([]);
            handleCloseGenerateModal();
            fetchData();
        } catch (error) {
            console.error("Error publishing payslips:", error);
            toast.error(error.response?.data?.error || error.response?.data?.detail || "Failed to publish payslips");
        } finally {
            setGenerating(false);
        }
    };

    /* ─────────────────────── Excel Download ─────────────────────── */
    const handleDownloadExcel = () => {
        if (!filteredData.length) {
            toast.warning("No records to download!");
            return;
        }

        const excelData = filteredData.map((item) => ({
            "Emp Code":                  item.employee?.emp_code || "",
            "Name":                      `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`.trim(),
            "Organisation":              item.employee?.organisation_name || "",
            "Department":                item.employee?.department_name || "",
            "Designation":               item.employee?.designation_name || "",
            "Month":                     item.month ? dayjs().month(item.month - 1).format("MMMM") : "",
            "Year":                      item.year || "",
            "Month Days":                item.month_days || "",
            "Days Payable":              item.days_payable || "",
            "Basic":                     item.basic_earned || "",
            "HRA":                       item.hra_earned || "",
            "Conveyance":                item.conveyance_earned || "",
            "Other Allowances":          item.other_allowance_earned || "",
            "Gross":                     item.gross_earned || "",
            "OT Hours":                  item.ot_hours || "",
            "OT Days":                   item.no_of_ot_days || "",
            "OT Pay":                    item.ot_pay || "",
            "Arrears":                   item.arrears || "",
            "PT":                        item.pt_pm || "",
            "Employer PF":               item.pf_employer || "",
            "Employer ESI":              item.esi_employer || "",
            "Employee PF":               item.pf_employee || "",
            "Employee ESI":              item.esi_employee || "",
            "Flexible Benefit":          item.flexible_benefit_allowance || "",
            "Late Days":                 item.late_days || "",
            "Late Deduction":            item.late_deduction || "",
            "LOP Days":                  item.lop_days || "",
            "LOP Deduction":             item.lop_deduction || "",
            "Salary Advance":            item.salary_advance || "",
            "Total Deductions":          item.total_deductions || "",
            "Net Pay":                   item.net_pay || "",
            "Net Pay in Words":          item.net_pay_in_words || "",
            "Salary Type":               item.salary_type || "",
            "Status":                    item.status || "",
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Final Salary Statement");
        XLSX.writeFile(wb, "Final_Salary_Statement.xlsx");
        toast.success("Excel downloaded successfully!");
    };

    /* ─────────────────────── Table Columns ─────────────────────── */
    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            fixed: "left",
            width: 120,
        },
        {
            title: "Name",
            key: "employee_name",
            fixed: "left",
            width: 200,
            render: (_, record) =>
                `${record.employee?.first_name || ""} ${record.employee?.last_name || ""}`.trim(),
        },
        {
            title: "Organisation",
            dataIndex: ["employee", "organisation_name"],
            key: "organisation_name",
            width: 180,
        },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
            key: "department_name",
            width: 180,
        },
        {
            title: "Designation",
            dataIndex: ["employee", "designation_name"],
            key: "designation_name",
            width: 180,
        },
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            width: 120,
            render: (m) => {
                const num = parseInt(m, 10);
                return num >= 1 && num <= 12 ? dayjs().month(num - 1).format("MMMM") : "-";
            },
        },
        { title: "Year",         dataIndex: "year",                    key: "year",         width: 100 },
        { title: "Month Days",   dataIndex: "month_days",              key: "month_days",   width: 110 },
        { title: "Days Payable", dataIndex: "days_payable",            key: "days_payable", width: 120 },
        { title: "Basic",        dataIndex: "basic_earned",            key: "basic_earned", width: 120 },
        { title: "HRA",          dataIndex: "hra_earned",              key: "hra_earned",   width: 120 },
        { title: "Conveyance",   dataIndex: "conveyance_earned",       key: "conveyance",   width: 120 },
        { title: "Other Allow.", dataIndex: "other_allowance_earned",  key: "other_allow",  width: 130 },
        { title: "Gross",        dataIndex: "gross_earned",            key: "gross_earned", width: 120 },
        { title: "OT Hours",     dataIndex: "ot_hours",                key: "ot_hours",     width: 110 },
        { title: "OT Days",      dataIndex: "no_of_ot_days",           key: "no_of_ot_days",width: 100 },
        { title: "OT Pay",       dataIndex: "ot_pay",                  key: "ot_pay",       width: 110 },
        { title: "Arrears",      dataIndex: "arrears",                 key: "arrears",      width: 100 },
        { title: "PT",           dataIndex: "pt_pm",                   key: "pt_pm",        width: 90  },
        { title: "Employer PF",  dataIndex: "pf_employer",             key: "pf_employer",  width: 120 },
        { title: "Employer ESI", dataIndex: "esi_employer",            key: "esi_employer", width: 120 },
        { title: "Employee PF",  dataIndex: "pf_employee",             key: "pf_employee",  width: 120 },
        { title: "Employee ESI", dataIndex: "esi_employee",            key: "esi_employee", width: 120 },
        { title: "Flex. Benefit",dataIndex: "flexible_benefit_allowance", key: "flex_benefit", width: 130 },
        { title: "Late Days",    dataIndex: "late_days",               key: "late_days",    width: 100 },
        { title: "Late Deduct.", dataIndex: "late_deduction",          key: "late_deduction",width: 120 },
        { title: "LOP Days",     dataIndex: "lop_days",                key: "lop_days",     width: 100 },
        { title: "LOP Deduct.",  dataIndex: "lop_deduction",           key: "lop_deduction",width: 120 },
        { title: "Salary Adv.",  dataIndex: "salary_advance",          key: "salary_advance",width: 120 },
        { title: "Total Deduct.",dataIndex: "total_deductions",        key: "total_deductions",width: 130 },
        { title: "Net Pay",      dataIndex: "net_pay",                 key: "net_pay",      width: 120 },
        { title: "Net Pay (Words)", dataIndex: "net_pay_in_words",     key: "net_pay_words",width: 220 },
        { title: "Salary Type",  dataIndex: "salary_type",             key: "salary_type",  width: 110 },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (text) => (
                <span style={{ fontWeight: 600, color: text === "Approved" ? "#52c41a" : "#faad14" }}>
                    {text || "-"}
                </span>
            ),
        },
    ];

    /* ─────────────────────── Render ─────────────────────── */
    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <Title level={4} style={{ marginTop: 20, color: "#0e497b", textTransform: "uppercase" }}>
                    Final Salary Statement
                </Title>
            </center>

            {/* Filters */}
            <Card style={{ margin: "0 20px 20px 20px" }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Organization"
                            style={{ width: "100%" }}
                            value={organizationNames.find(o => o.name === filters.organization)?.id}
                            onChange={handleOrgChange}
                            allowClear
                        >
                            {organizationNames.map(org => (
                                <Select.Option key={org.id} value={org.id}>{org.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Department"
                            style={{ width: "100%" }}
                            value={departmentNames.find(d => d.name === filters.department)?.id}
                            onChange={handleDeptChange}
                            allowClear
                            disabled={!filters.organization}
                        >
                            {departmentNames.map(dep => (
                                <Select.Option key={dep.id} value={dep.id}>{dep.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Designation"
                            style={{ width: "100%" }}
                            value={designationNames.find(d => d.name === filters.designation)?.id}
                            onChange={handleDesignationChange}
                            allowClear
                            disabled={!filters.department}
                        >
                            {designationNames.map(des => (
                                <Select.Option key={des.id} value={des.id}>{des.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Emp Code"
                            value={filters.emp_code}
                            onChange={e => setFilters({ ...filters, emp_code: e.target.value })}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Name"
                            value={filters.name}
                            onChange={e => setFilters({ ...filters, name: e.target.value })}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            placeholder="Month"
                            style={{ width: "100%" }}
                            value={filters.month || undefined}
                            onChange={v => setFilters({ ...filters, month: v })}
                            allowClear
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={i + 1}>
                                    {dayjs().month(i).format("MMMM")}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Year"
                            type="number"
                            value={filters.year}
                            onChange={e => setFilters({ ...filters, year: e.target.value })}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={2}>
                        <Button style={{ width: "100%" }} onClick={handleResetFilters}>
                            Reset
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Action Bar + Table */}
            <Card style={{ margin: "0 20px 20px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", gap: 12 }}>
                        <Card size="small" style={{ borderRadius: 8, background: "#f0f5ff", border: "1px solid #adc6ff" }}>
                            <span style={{ color: "#2f54eb", fontWeight: "bold" }}>Total Records: </span>
                            <span style={{ fontSize: 16, fontWeight: "bold" }}>{filteredData.length}</span>
                        </Card>
                        {selectedRowKeys.length > 0 && (
                            <Card size="small" style={{ borderRadius: 8, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
                                <span style={{ color: "#52c41a", fontWeight: "bold" }}>Selected: </span>
                                <span style={{ fontSize: 16, fontWeight: "bold" }}>{selectedRowKeys.length}</span>
                            </Card>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <Button
                            icon={<DownloadOutlined />}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", color: "white", fontWeight: "500" }}
                            onClick={handleDownloadExcel}
                        >
                            Download Excel
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", fontWeight: "500" }}
                            onClick={handleOpenGenerateModal}
                            disabled={selectedRowKeys.length === 0}
                        >
                            Publish Payslip ({selectedRowKeys.length})
                        </Button>
                    </div>
                </div>

                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    loading={loading}
                    scroll={{ x: 3800 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white", textAlign: "center" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>

            {/* Publish Payslip Modal */}
            <Modal
                open={isGenerateModalVisible}
                onCancel={handleCloseGenerateModal}
                title={`Publish Payslip — ${selectedRowKeys.length} record(s) selected`}
                footer={null}
                width={480}
            >
                <Form layout="vertical" form={generateForm}>
                    <Form.Item
                        label="Select Month and Year"
                        name="month_year"
                        rules={[{ required: true, message: "Please select month and year!" }]}
                    >
                        <DatePicker
                            picker="month"
                            style={{ width: "100%" }}
                            format="MMMM YYYY"
                            placeholder="Select Month and Year"
                        />
                    </Form.Item>
                    <div style={{ textAlign: "right" }}>
                        <Button onClick={handleCloseGenerateModal} style={{ marginRight: 10 }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={generating}
                            onClick={handleGeneratePayslip}
                            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", fontWeight: "500" }}
                        >
                            Publish
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}
